/* eslint-disable @repo/internal/react/require-jsdoc */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode } from 'react';

import { css, jsx } from '@compiled/react';

import Heading from '@atlaskit/heading/heading';
import Link from '@atlaskit/link/link';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { Hide, Show } from '@atlaskit/primitives/responsive';

import { GridContainer } from '../grid-container';
import { AtlassianDesignLogo } from '../navigation/atlassian-design-logo';
import { AtlassianDesignLogoSmall } from '../navigation/atlassian-design-logo-small';

const FooterLink = ({ href, children }: { href: string; children: ReactNode }) => {
	return (
		<Link href={href} target={href.startsWith('http') ? '_blank' : undefined} appearance="subtle">
			{children}
		</Link>
	);
};

interface FooterProps {
	columns: FooterColumn[];
	row: FooterRowItem[];
	copyrightText?: string;
	isHome?: boolean;
}

export interface FooterColumn {
	title: string;
	items: { label: string; href: string }[];
}

export interface FooterRowItem {
	label: string;
	href: string;
}

const rootStylesPadding = xcss({
	paddingInline: 'space.300',
});

const rootStyles = xcss({
	paddingBlock: 'space.400',
	width: '100%',
	borderBlockStartColor: 'color.border',
	borderBlockStartWidth: 'border.width',
	borderBlockStartStyle: 'solid',
});

const gridContainerContentStyles = xcss({
	paddingBlock: 'space.400',
	gridColumn: 'content-start / content-end',
});

const gridStylesHome = xcss({
	gridColumn: 'content-start / content-end',
});

const responsiveFooterWrapper = xcss({
	flexDirection: 'column',
	gap: 'space.300',
	paddingBlockStart: 'space.300',
	width: '100%',
	'@media (min-width: 48rem)': {
		display: 'grid',
		gridTemplateRows: 'auto auto',
		gridTemplateColumns: '1fr 1fr',
		gap: 'space.200',
		alignItems: 'center',
	},
	'@media (min-width: 90rem)': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 'space.400',
	},
});

const responsiveFooterWrapperHome = xcss({
	gridColumn: 'content-start / content-end',
});

const dividingLineWrapperHome = xcss({
	width: '100%',
	borderBlockStartWidth: 'border.width',
	borderBlockStartStyle: 'solid',
	borderBlockStartColor: 'color.border',
});

const gridStyles = xcss({
	display: 'grid',
	width: '100%',
	gridTemplateColumns: '1fr',
	justifyContent: 'space-between',
	'@media (min-width: 30rem)': {
		gridTemplateColumns: 'repeat(3, 1fr)',
	},
	'@media (min-width: 48rem)': {
		gridTemplateColumns: 'repeat(4, 1fr)',
	},
	gap: 'space.200',
});

const columnStyles = xcss({
	'@media (min-width: 30rem)': {
		minWidth: 'max-content',
	},
	minWidth: 'unset',
	padding: 'space.0',
});

const linksGoodDesignRow = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.200',
	'@media (min-width: 48rem)': {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gridColumn: '1 / -1',
	},
	'@media (min-width: 90rem)': {
		flex: 1,
		justifyContent: 'flex-start',
		gap: 'space.400',
		gridColumn: 'auto',
	},
});

const linksRowOnly = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.150',
	'@media (min-width: 30rem)': {
		flexDirection: 'row',
		gap: 'space.300',
	},
	'@media (min-width: 48rem)': {
		gap: 'space.500',
	},
});

const listWrapper = xcss({
	padding: 'space.0',
	margin: 'space.0',
	listStyle: 'none',
});

const listItemWrapper = xcss({
	margin: 'space.0',
});

const goodDesignResponsive = xcss({
	alignSelf: 'flex-start',
	marginBlockStart: 'space.200',
	'@media (min-width: 48rem)': {
		alignSelf: 'center',
		marginBlockStart: 'space.0',
	},
	'@media (min-width: 90rem)': {
		marginInlineStart: 'auto',
	},
});

const FooterGrouping = ({ column }: { column: FooterColumn }) => {
	const { title, items } = column;
	return (
		<Box as="li" xcss={listItemWrapper}>
			<Stack xcss={columnStyles} space="space.150">
				<Heading as="span" size="small">
					{title}
				</Heading>
				<Stack as="ul" space="space.150" xcss={listWrapper}>
					{items.map(({ label, href }) => (
						<Box as="li" key={href} xcss={listItemWrapper}>
							<FooterLink href={href}>{label}</FooterLink>
						</Box>
					))}
				</Stack>
			</Stack>
		</Box>
	);
};

export const Footer = ({ columns, row, copyrightText, isHome }: FooterProps): React.JSX.Element => {
	const mainContent = (
		<Stack space="space.200" alignInline="start" xcss={isHome && gridContainerContentStyles}>
			<Hide above="xs">
				<AtlassianDesignLogoSmall />
			</Hide>
			<Show above="xs">
				<AtlassianDesignLogo />
			</Show>
			<Box as="ul" xcss={[gridStyles, isHome && gridStylesHome, listWrapper]}>
				<FooterGrouping column={columns[0]} />
				<FooterGrouping column={columns[1]} />
				<FooterGrouping column={columns[2]} />
				<FooterGrouping column={columns[3]} />
			</Box>
		</Stack>
	);

	const footerRow = (
		<Flex xcss={[responsiveFooterWrapper]}>
			<Box xcss={linksGoodDesignRow}>
				<Inline xcss={linksRowOnly}>
					<Text color="color.text.subtlest">
						{copyrightText || `© ${new Date().getFullYear()} Atlassian`}
					</Text>
					{row.map(({ label, href }) => (
						<FooterLink key={href} href={href}>
							{label}
						</FooterLink>
					))}
				</Inline>
				<Box xcss={goodDesignResponsive}>
					<GoodDesignSVG />
				</Box>
			</Box>
		</Flex>
	);

	return isHome ? (
		<Box xcss={rootStyles} as="footer">
			<GridContainer spacing="none">{mainContent}</GridContainer>
			<Box xcss={dividingLineWrapperHome}>
				<GridContainer spacing="none">
					<Box xcss={responsiveFooterWrapperHome}>{footerRow}</Box>
				</GridContainer>
			</Box>
		</Box>
	) : (
		<Box xcss={[rootStyles, rootStylesPadding]} as="footer">
			<Stack space="space.200" alignInline="start">
				{mainContent}
				<Box xcss={dividingLineWrapperHome}>{footerRow}</Box>
			</Stack>
		</Box>
	);
};

/**
 * Black on light, white on dark. The swap is in CSS, keyed on the `data-color-mode` the pre-paint
 * script has already set on `<html>`, so the prerendered markup is identical for every reader.
 * Choosing the fill in JS made the server emit one value and a dark reader's first client render
 * another, a hydration mismatch React 18 does not patch.
 */
const goodDesignStyles = css({
	'--good-design-fill': 'black',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- swaps the color by color mode without re-rendering
	'html[data-color-mode="dark"] &': {
		'--good-design-fill': 'white',
	},
});

const GoodDesignSVG = () => {
	return (
		<svg
			css={goodDesignStyles}
			width="128"
			height="48"
			viewBox="0 0 128 48"
			fill="none"
			aria-label="Good Design Award - Gold winner"
			xmlns="http://www.w3.org/2000/svg"
		>
			<g clipPath="url(#clip0_6857_17385)">
				<path
					d="M47.0343 14.583L19.5133 41.8746H18.778L0.710693 23.958V47.8954H24.8284C38.1478 47.8954 48.9461 37.1871 48.9461 23.9788C48.9671 20.6455 48.2738 17.4788 47.0343 14.583Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M19.5133 30.583L42.5595 7.72897C38.1478 2.99982 31.8452 0.0415039 24.8494 0.0415039H0.794728H0.710693V23.9581H12.0973L18.757 30.5622H19.5133V30.583Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M103.044 14.0833V24.9583L103.212 25.125H105.145L105.313 24.9583V17.6042H105.481L109.83 24.9583L110.145 25.125H112.393L112.561 24.9583V14.0833L112.393 13.9167H110.46L110.292 14.0833V21.4375H110.124L105.775 14.0833L105.46 13.9167H103.212L103.044 14.0833ZM101.531 24.9583V19.2083L101.363 19.0416H96.3632L96.1951 19.2083V20.8125L96.3632 20.9791H99.0943L99.2624 21.1458C99.2624 22.4166 98.4641 23.375 96.6783 23.375C94.5775 23.375 93.464 21.7708 93.464 19.5416C93.464 17.3125 94.5985 15.7083 96.6783 15.7083C98.443 15.7083 98.9262 16.5 99.2624 17.1458L99.4304 17.3125H101.363L101.531 17.1458C101.216 15.5417 99.9136 13.7917 96.6993 13.7917C93.317 13.7917 91.2161 16.1875 91.2161 19.5416C91.2161 22.8958 93.317 25.2916 96.5313 25.2916C98.296 25.2916 99.1153 24.5 99.4304 24.1666H99.5985L99.7666 24.9583L99.9346 25.125H101.384L101.531 24.9583ZM89.8296 24.9583V14.0833L89.6615 13.9167H87.7287L87.5607 14.0833V24.9583L87.7287 25.125H89.6615L89.8296 24.9583ZM86.1321 21.9166C86.1321 19.6875 84.3674 18.7291 82.0985 18.3958C79.8506 18.0833 79.1993 17.75 79.1993 16.7917C79.1993 16.1458 79.6825 15.6667 81.1321 15.6667C82.8968 15.6667 83.38 16.1458 83.38 17.1042L83.5481 17.2708H85.4808L85.6489 17.1042C85.6489 15.1875 84.3674 13.75 81.3001 13.75C78.2329 13.75 76.9514 15.1875 76.9514 16.7917C76.9514 19.0208 78.7161 19.8333 80.985 20.1458C83.2329 20.4583 83.8842 20.7916 83.8842 21.8958C83.8842 22.8541 83.0859 23.3333 81.6363 23.3333C79.7035 23.3333 79.0522 22.6875 79.0522 21.5833L78.8842 21.4166H76.9304L76.7623 21.5833C76.7623 23.6666 78.2119 25.2708 81.4472 25.2708C84.8296 25.2708 86.1321 23.6666 86.1321 21.9166ZM75.7119 24.9583V23.3541L75.5438 23.1875H70.2287L70.0606 23.0208V20.4583L70.2287 20.2916H75.2287L75.3968 20.125V18.5208L75.2287 18.3541H70.2287L70.0606 18.1875V15.9583L70.2287 15.7917H75.5438L75.7119 15.625V14.0208L75.5438 13.8542H67.9598L67.7917 14.0208V24.8958L67.9598 25.0625H75.5438L75.7119 24.9583ZM64.4304 19.5208C64.4304 22.0833 63.632 23.2083 61.3631 23.2083H59.9135L59.7455 23.0416V16L59.9135 15.8333H61.3631C63.611 15.8333 64.4304 16.9583 64.4304 19.5208ZM66.6783 19.5208C66.6783 16 65.2287 13.9167 61.1951 13.9167H57.6446L57.4766 14.0833V24.9583L57.6446 25.125H61.1951C65.2287 25.125 66.6783 23.0416 66.6783 19.5208Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M99.6395 5.74998C99.6395 8.31246 98.8412 9.43746 96.5723 9.43746H95.1227L94.9546 9.2708V2.22916L95.1227 2.06249H96.5723C98.8202 2.08332 99.6395 3.20832 99.6395 5.74998ZM101.887 5.74998C101.887 2.22916 100.438 0.145834 96.4042 0.145834H92.8538L92.6857 0.312499V11.1875L92.8538 11.3541H96.4042C100.438 11.3541 101.887 9.27079 101.887 5.74998ZM91.3202 5.74998C91.3202 2.39582 89.2193 0 85.837 0C82.4546 0 80.3538 2.39582 80.3538 5.74998C80.3538 9.10413 82.4546 11.5 85.837 11.5C89.2193 11.5208 91.3202 9.12496 91.3202 5.74998ZM89.0513 5.74998C89.0513 7.97913 87.9168 9.58329 85.837 9.58329C83.7361 9.58329 82.6227 7.97913 82.6227 5.74998C82.6227 3.52082 83.7571 1.91666 85.837 1.91666C87.9378 1.91666 89.0513 3.52082 89.0513 5.74998ZM79.2193 5.74998C79.2193 2.39582 77.1185 0 73.7361 0C70.3537 0 68.2529 2.39582 68.2529 5.74998C68.2529 9.10413 70.3537 11.5 73.7361 11.5C77.1185 11.5208 79.2193 9.12496 79.2193 5.74998ZM76.9714 5.74998C76.9714 7.97913 75.8369 9.58329 73.7571 9.58329C71.6563 9.58329 70.5428 7.97913 70.5428 5.74998C70.5428 3.52082 71.6773 1.91666 73.7571 1.91666C75.8369 1.91666 76.9714 3.52082 76.9714 5.74998ZM67.0134 11.1875V5.43748L66.8453 5.27081H61.8453L61.6772 5.43748V7.04164L61.8453 7.2083H64.5764L64.7445 7.37497C64.7445 8.6458 63.9462 9.60413 62.1604 9.60413C60.0596 9.60413 58.9461 7.99997 58.9461 5.77081C58.9461 3.54165 60.0806 1.93749 62.1604 1.93749C63.9252 1.93749 64.4083 2.72916 64.7445 3.37499L64.9125 3.54165H66.8453L67.0134 3.37499C66.6983 1.77083 65.3957 0.0208329 62.1814 0.0208329C58.7991 0.0208329 56.6982 2.41666 56.6982 5.77081C56.6982 9.12496 58.7991 11.5208 62.0134 11.5208C63.7781 11.5208 64.5974 10.7291 64.9125 10.3958H65.0806L65.2487 11.1875L65.4168 11.3541H66.8663L67.0134 11.1875Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M108.778 32.9581C108.778 35.5206 107.98 36.6248 105.711 36.6248H104.261L104.093 36.4581V29.4373L104.261 29.2707H105.711C107.959 29.2707 108.778 30.3957 108.778 32.9581ZM111.026 32.9581C111.026 29.4373 109.577 27.3748 105.543 27.3748H101.992L101.824 27.5415V38.3956L101.992 38.5623H105.543C109.577 38.5415 111.026 36.4581 111.026 32.9581ZM98.337 30.7082C98.337 31.8332 97.6858 32.1457 96.7194 32.1457H93.6521L93.4841 31.979V29.4165L93.6521 29.2498H96.7194C97.6858 29.2707 98.337 29.604 98.337 30.7082ZM100.585 30.7082C100.585 28.479 98.9673 27.354 96.7194 27.354H91.4252L91.2572 27.5207V38.3748L91.4252 38.5415H93.358L93.5261 38.3748V34.229L93.6942 34.0623H95.3118L95.6269 34.229L98.8412 38.3748L99.1563 38.5415H100.123L100.291 38.3748V36.7706L98.19 34.0623V33.8956C99.4715 33.5831 100.585 32.479 100.585 30.7082ZM86.7404 34.0623L86.5723 34.229H83.8412L83.6731 34.0623L85.1227 30.229H85.2908L86.7404 34.0623ZM90.1227 38.3748V36.7706L86.4252 27.5207L86.2572 27.354H84.1563L83.9882 27.5207L80.2908 36.7706V38.3748L80.4588 38.5415H81.9084L82.0765 38.3748L82.8748 36.2915L83.1899 36.1248H87.2236L87.5387 36.2915L88.337 38.3748L88.5051 38.5415H89.9547L90.1227 38.3748ZM72.5176 27.354L72.3496 27.5207L70.2487 34.854H70.0806L68.463 27.5207L68.2949 27.354H66.6773L66.5092 27.5207V29.1248L68.9252 38.3748L69.0933 38.5415H71.1941L71.3622 38.3748L73.463 30.7082H73.6311L75.7319 38.3748L75.9 38.5415H78.0008L78.1689 38.3748L80.5849 29.1248V27.5207L80.4168 27.354H78.7992L78.6311 27.5207L77.0134 34.854H76.8454L74.7445 27.5207L74.5765 27.354H72.5176ZM63.526 34.0623L63.3579 34.229H60.6268L60.4588 34.0623L61.9084 30.229H62.0764L63.526 34.0623ZM66.9084 38.3748V36.7706L63.2109 27.5207L63.0428 27.354H60.942L60.7739 27.5207L57.0764 36.7706V38.3748L57.2445 38.5415H58.6941L58.8621 38.3748L59.6605 36.2915L59.9756 36.1248H64.0092L64.3243 36.2915L65.1227 38.3748L65.2907 38.5415H66.7403L66.9084 38.3748Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M114.618 27.6665H113.736L113.673 27.729V28.479L113.736 28.5415H114.618C114.933 28.5415 115.059 28.354 115.059 28.104C115.059 27.854 114.933 27.6665 114.618 27.6665ZM115.375 29.5415V29.7915L115.312 29.854H115.249L115.122 29.7915L114.492 28.9165L114.366 28.854H113.736L113.673 28.9165V29.7915L113.61 29.854H113.358L113.295 29.7915V27.4165L113.358 27.354H114.618C115.059 27.354 115.438 27.604 115.438 28.104C115.438 28.479 115.185 28.729 114.87 28.7915V28.854L115.375 29.5415ZM114.24 30.5415C115.312 30.5415 116.194 29.6665 116.194 28.604C116.194 27.5415 115.312 26.6665 114.24 26.6665C113.169 26.6665 112.286 27.5415 112.286 28.604C112.286 29.6665 113.169 30.5415 114.24 30.5415ZM114.24 26.354C115.501 26.354 116.509 27.354 116.509 28.604C116.509 29.854 115.501 30.854 114.24 30.854C112.98 30.854 111.971 29.854 111.971 28.604C111.971 27.354 112.98 26.354 114.24 26.354Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M63.4828 47.8956H62.5585L62.4534 47.7915L62.3484 47.2706H62.2433C62.0332 47.479 61.529 47.979 60.3946 47.979C58.3357 47.979 56.9912 46.4373 56.9912 44.2915C56.9912 42.1457 58.3357 40.604 60.4996 40.604C62.5584 40.604 63.3778 41.729 63.5879 42.7498L63.4828 42.8957H62.2433L62.1383 42.7915C61.9282 42.3748 61.6131 41.8748 60.4786 41.8748C59.1341 41.8748 58.4198 42.8957 58.4198 44.3332C58.4198 45.7706 59.1341 46.7915 60.4786 46.7915C61.6131 46.7915 62.1383 46.1873 62.1383 45.354L62.0332 45.2498H60.2895L60.1845 45.1456V44.1248L60.2895 44.0207H63.4828L63.5879 44.1248V47.8123L63.4828 47.8956Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M68.0209 48C65.857 48 64.5125 46.4583 64.5125 44.3125C64.5125 42.1667 65.857 40.625 68.0209 40.625C70.1847 40.625 71.5293 42.1667 71.5293 44.3125C71.5293 46.4583 70.1847 48 68.0209 48ZM68.0209 41.8542C66.6763 41.8542 65.962 42.875 65.962 44.3125C65.962 45.75 66.6763 46.7708 68.0209 46.7708C69.3654 46.7708 70.0797 45.75 70.0797 44.3125C70.0797 42.8958 69.3654 41.8542 68.0209 41.8542Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M77.3697 47.8956H72.7269L72.6218 47.7915V40.8332L72.7269 40.729H73.9664L74.0714 40.8332V46.5623L74.1765 46.6665H77.3697L77.4748 46.7706V47.7915L77.3697 47.8956Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M80.6056 47.8956H78.3367L78.2317 47.7915V40.8332L78.3367 40.729H80.6056C83.1897 40.729 84.1141 42.0623 84.1141 44.3123C84.093 46.5623 83.1687 47.8956 80.6056 47.8956ZM80.6897 41.9582H79.7653L79.6603 42.0623V46.5623L79.7653 46.6665H80.6897C82.1393 46.6665 82.6435 45.9581 82.6435 44.3123C82.6435 42.6665 82.1393 41.9582 80.6897 41.9582Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M92.7687 40.729L92.8737 40.8332L94.2183 45.5415H94.3233L95.3527 40.8332L95.4578 40.729H96.4872L96.5922 40.8332V41.854L95.0376 47.7915L94.9325 47.8956H93.588L93.483 47.7915L92.1384 42.8748H92.0334L90.6888 47.7915L90.5838 47.8956H89.2393L89.1342 47.7915L87.5796 41.854V40.8332L87.6846 40.729H88.714L88.8191 40.8332L89.8485 45.5415H89.9535L91.2981 40.8332L91.4031 40.729H92.7687Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M98.9654 47.8956H97.7259L97.6208 47.7915V40.8332L97.7259 40.729H98.9654L99.0704 40.8332V47.7915L98.9654 47.8956Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M100.646 40.729H102.095L102.305 40.8332L105.078 45.5415H105.183V40.8332L105.288 40.729H106.528L106.633 40.8332V47.7915L106.528 47.8956H105.078L104.868 47.7915L102.095 43.0832H101.99V47.7915L101.885 47.8956H100.646L100.541 47.7915V40.8332L100.646 40.729Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M108.209 40.729H109.659L109.869 40.8332L112.642 45.5415H112.747V40.8332L112.852 40.729H114.091L114.196 40.8332V47.7915L114.091 47.8956H112.642L112.432 47.7915L109.659 43.0832H109.554V47.7915L109.449 47.8956H108.209L108.104 47.7915V40.8332L108.209 40.729Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M120.627 47.8956H115.774L115.669 47.7915V40.8332L115.774 40.729H120.627L120.732 40.8332V41.854L120.627 41.9582H117.224L117.119 42.0623V43.4998L117.224 43.604H120.417L120.522 43.7082V44.729L120.417 44.8331H117.224L117.119 44.9373V46.5831L117.224 46.6873H120.627L120.732 46.7915V47.8123L120.627 47.8956Z"
					fill="var(--good-design-fill)"
				/>
				<path
					d="M126.446 44.9373V45.0415L127.79 46.7706V47.7915L127.685 47.8956H127.076L126.866 47.7915L124.807 45.1248L124.597 45.0207H123.568L123.463 45.1248V47.7915L123.357 47.8956H122.118L122.013 47.7915V40.8332L122.118 40.729H125.521C126.971 40.729 128 41.4373 128 42.8748C128 44.0207 127.286 44.729 126.446 44.9373ZM125.521 41.9582H123.568L123.463 42.0623V43.7082L123.568 43.8123H125.521C126.131 43.8123 126.551 43.604 126.551 42.8957C126.551 42.1665 126.152 41.9582 125.521 41.9582Z"
					fill="var(--good-design-fill)"
				/>
			</g>
			<defs>
				<clipPath id="clip0_6857_17385">
					<rect width="127.289" height="47.9999" fill="white" transform="translate(0.710693)" />
				</clipPath>
			</defs>
		</svg>
	);
};
